import { FC, useMemo } from 'react'

import styled from 'styled-components'

import {
  CampaignPlatformTypeEnum,
  SpotifyCampaignMatchingType,
} from 'src/Types/campaigns'
import { MAX_MOBILE_WIDTH, MAX_TABLET_WIDTH } from 'src/Styled/variables'
import { PaymentMethodEnum } from 'src/Constants/enums'
import { WalletExpressCheckoutProps } from 'src/Components/WalletExpressCheckout'

import { ScheduleSummaryDetails } from './ScheduleSummaryDetails'
import { ScheduleSummaryPromocode } from './ScheduleSummaryPromocode'
import { ScheduleSummaryButton } from './ScheduleSummaryButton'
import { ArtistProtectionProgram } from './ArtistProtectionProgram'
import { AutoRenewalInfoText } from './AutoRenewalInfoText'
import { ScheduleSummaryPrioritizedLaunch } from './ScheduleSummaryDetails/ScheduleSummaryStartDateRow'
import {
  ThresholdCouponBanner,
  type DiscountTotalsLineProps,
  type ThresholdCouponBannerProps,
} from './ThresholdCouponBanner'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 23px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  box-shadow: ${({ theme }) => theme.shadow.card};

  @media screen and (min-width: ${MAX_TABLET_WIDTH}) {
    width: 400px;
  }
`

const Title = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
`

const Wrapper = styled.div<{ $tooltipPadding: number }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    padding-bottom: ${({ $tooltipPadding }) => `${$tooltipPadding}px`};
  }
  @media screen and (min-width: 768px) and (max-width: ${MAX_TABLET_WIDTH}) {
    flex: 1 0 0;
    min-width: 0;
    max-width: 324px;
  }
  @media screen and (min-width: ${MAX_TABLET_WIDTH}) {
    width: 400px;
  }
`

export type CouponApplyResult = {
  coupon: {
    error: {
      message: string
      param: unknown
    } | null
    isBonusCoupon?: boolean
  }
}

export type ThresholdCouponSummary = {
  banner: ThresholdCouponBannerProps | null
  discountLine: DiscountTotalsLineProps | null
}

export interface ScheduleSummaryProps {
  artistName: string
  trackName: string
  platform: CampaignPlatformTypeEnum
  price: string
  vat?: string
  discount?: string
  total: string
  balance: string
  showPromocode: boolean
  paymentLoading: boolean
  onApplyPromocode: (coupon: string) => Promise<CouponApplyResult | undefined>
  onConfirm: () => void
  showTooltip: boolean
  countdown?: string
  confirmDisabled?: boolean
  isBalanceHidden?: boolean
  isProtectionHidden?: boolean
  isAutoRenewalInfoTextVisible?: boolean
  matchingType: SpotifyCampaignMatchingType | null
  paymentMethod?: PaymentMethodEnum
  trackCover?: string
  isLoading?: boolean
  walletCheckout?: Omit<WalletExpressCheckoutProps, 'selectedWalletMethod'>
  isPreRelease?: boolean
  prioritizedLaunch?: ScheduleSummaryPrioritizedLaunch
  thresholdCoupon?: ThresholdCouponSummary
}

const CHECKOUT_TOOLTIP_PADDING = 200
const DEFAULT_PADDING = 50

export const ScheduleSummary: FC<ScheduleSummaryProps> = (props) => {
  const {
    onApplyPromocode,
    onConfirm,
    showPromocode,
    showTooltip,
    countdown,
    confirmDisabled,
    paymentLoading,
    isProtectionHidden,
    isAutoRenewalInfoTextVisible,
    isBalanceHidden,
    platform,
    paymentMethod,
    isLoading,
    walletCheckout,
    ...restProps
  } = props

  const couponBanner = restProps.thresholdCoupon?.banner
  const isCouponOffered = couponBanner?.state === 'offered'
  const isCouponAttached = Boolean(couponBanner) && !isCouponOffered

  const tooltipPadding = useMemo(() => {
    if (showTooltip) {
      return CHECKOUT_TOOLTIP_PADDING
    }

    return DEFAULT_PADDING
  }, [showTooltip])

  return (
    <Wrapper $tooltipPadding={tooltipPadding}>
      <Container>
        <Title>Order summary</Title>
        {isCouponAttached && couponBanner && (
          <ThresholdCouponBanner {...couponBanner} />
        )}
        <ScheduleSummaryDetails
          {...restProps}
          platform={platform}
          isBalanceHidden={isBalanceHidden}
        />
        {showPromocode && !isCouponAttached && (
          <ScheduleSummaryPromocode onApplyPromocode={onApplyPromocode} />
        )}
        {isCouponOffered && couponBanner && (
          <ThresholdCouponBanner {...couponBanner} />
        )}
        <ScheduleSummaryButton
          paymentLoading={paymentLoading}
          total={restProps.total}
          countdown={countdown}
          showTooltip={showTooltip}
          confirmDisabled={confirmDisabled}
          onConfirm={onConfirm}
          isBalanceHidden={isBalanceHidden}
          paymentMethod={paymentMethod}
          walletCheckout={walletCheckout}
          isLoading={isLoading}
        />
      </Container>
      {!isProtectionHidden && (
        <ArtistProtectionProgram
          platform={platform}
          matchingType={restProps.matchingType}
        />
      )}
      {isAutoRenewalInfoTextVisible && <AutoRenewalInfoText />}
    </Wrapper>
  )
}
